/** Typed stores with `subscribe` and `dispose` (no framework). */

export interface Store<T> {
  get(): T;
  set(value: T): void;
  update(change: (value: T) => T): void;
  subscribe(listener: (value: T) => void): () => void;
  dispose(): void;
}

/** Create a store; listeners run on a change (by `Object.is`), never after `dispose`. */
export function createStore<T>(initial: T): Store<T> {
  let current = initial;
  let closed = false;
  const listeners = new Set<(value: T) => void>();
  const set = (value: T): void => {
    if (closed || Object.is(value, current)) {
      return;
    }
    current = value;
    for (const listener of [...listeners]) {
      listener(value);
    }
  };
  return {
    get: () => current,
    set,
    update: (change) => {
      set(change(current));
    },
    subscribe: (listener) => {
      if (closed) {
        return () => undefined;
      }
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
    dispose: () => {
      closed = true;
      listeners.clear();
    },
  };
}
